import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Plus, Minus } from 'lucide-react';

interface BlockProps {
  color: string;
  isLight?: boolean; // Whether this is a light color that needs special styling
  index?: number;
  isFirst?: boolean;
  isLast?: boolean;
  onAdd?: () => void;
  onRemove?: () => void;
  hideRemove?: boolean; // Whether to hide the remove button on the first block
  interactive?: boolean;
}

export const Block = React.forwardRef<HTMLDivElement, BlockProps>(
  (
    {
      color,
      isLight = false,
      index = 0,
      isFirst = false,
      isLast = false,
      onAdd,
      onRemove,
      hideRemove = false,
      interactive = true,
    },
    ref,
  ) => {
    const [hoveredSide, setHoveredSide] = useState<'left' | 'right' | null>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!interactive) {
        return;
      }
      // Don't allow hovering on left side if remove is disabled
      if ((!isFirst || hideRemove) && !isLast) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const halfWidth = rect.width / 2;

      if (isFirst && !hideRemove && x < halfWidth) {
        setHoveredSide('left');
      } else if (isLast && x >= halfWidth) {
        setHoveredSide('right');
      } else {
        setHoveredSide(null);
      }
    };

    const handleMouseLeave = () => {
      setHoveredSide(null);
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!interactive) {
        return;
      }
      if (!isFirst && !isLast) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const halfWidth = rect.width / 2;

      if (isFirst && x < halfWidth && onRemove) {
        onRemove();
      } else if (isLast && x >= halfWidth && onAdd) {
        onAdd();
      }
    };

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.5 }}
        transition={{
          duration: 0.3,
          ease: 'easeOut',
          delay: index * 0.02,
        }}
        className="tw-w-16 tw-h-16 tw-flex-shrink-0 tw-relative tw-overflow-hidden"
        style={{
          backgroundColor: color,
          borderRadius: 'var(--radius-sm)',
          boxShadow: 'var(--elevation-sm)',
          cursor: interactive && hoveredSide ? 'pointer' : 'default',
          // For light colors, use a visible border instead of border-card
          border: isLight ? '2px solid var(--border)' : '2px solid var(--card)',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {/* Left half overlay for minus */}
        {interactive && isFirst && !hideRemove && (
          <motion.div
            className="tw-absolute tw-inset-0 tw-w-1/2 tw-left-0 tw-flex tw-items-center tw-justify-center"
            initial={{ opacity: 0.5 }}
            animate={{
              opacity: hoveredSide === 'left' ? 1 : 0.5,
              backgroundColor: hoveredSide === 'left' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)',
            }}
            transition={{ duration: 0.2 }}
          >
            <Minus
              className="tw-w-6 tw-h-6"
              strokeWidth={3}
              style={{
                // For light colors, use dark icons; for dark colors, use white icons
                color: isLight ? 'var(--foreground)' : 'white',
              }}
            />
          </motion.div>
        )}

        {/* Right half overlay for plus */}
        {interactive && isLast && (
          <motion.div
            className="tw-absolute tw-inset-0 tw-w-1/2 tw-right-0 tw-flex tw-items-center tw-justify-center"
            style={{ left: '50%' }}
            initial={{ opacity: 0.5 }}
            animate={{
              opacity: hoveredSide === 'right' ? 1 : 0.5,
              backgroundColor: hoveredSide === 'right' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)',
            }}
            transition={{ duration: 0.2 }}
          >
            <Plus
              className="tw-w-6 tw-h-6"
              strokeWidth={3}
              style={{
                // For light colors, use dark icons; for dark colors, use white icons
                color: isLight ? 'var(--foreground)' : 'white',
              }}
            />
          </motion.div>
        )}
      </motion.div>
    );
  },
);

Block.displayName = 'Block';
